import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 20,
    fontFamily: 'Helvetica',
    fontSize: 7.5,
    color: '#1e293b'
  },
  headerBanner: {
    backgroundColor: '#155e75',
    color: '#ffffff',
    padding: 8,
    borderRadius: 3,
    marginBottom: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  headerTitle: {
    fontSize: 11.5,
    fontWeight: 'bold',
    color: '#ffffff',
    textTransform: 'uppercase'
  },
  headerSubtitle: {
    fontSize: 7.5,
    color: '#a5f3fc',
    marginTop: 2
  },
  badge: {
    backgroundColor: '#0891b2',
    color: '#ffffff',
    paddingVertical: 3,
    paddingHorizontal: 6,
    borderRadius: 2,
    fontSize: 7.5,
    fontWeight: 'bold'
  },
  grid2: {
    flexDirection: 'row',
    gap: 6,
    marginBottom: 6
  },
  panel: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 3,
    padding: 5,
    backgroundColor: '#f8fafc'
  },
  panelTitle: {
    fontSize: 7.5,
    fontWeight: 'bold',
    color: '#0e7490',
    borderBottomWidth: 1,
    borderBottomColor: '#cbd5e1',
    paddingBottom: 2,
    marginBottom: 4,
    textTransform: 'uppercase'
  },
  fieldRow: {
    flexDirection: 'row',
    marginBottom: 2.2,
    alignItems: 'flex-start'
  },
  fieldLabel: {
    fontSize: 6.8,
    fontWeight: 'bold',
    color: '#475569',
    width: '45%'
  },
  fieldValue: {
    fontSize: 6.8,
    color: '#0f172a',
    width: '55%'
  },
  passText: {
    color: '#15803d',
    fontWeight: 'bold'
  },
  signaturesPanel: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 4,
    borderTopWidth: 1,
    borderTopColor: '#cbd5e1',
    paddingTop: 6
  },
  signatureBox: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 3,
    padding: 5,
    backgroundColor: '#ffffff'
  },
  footer: {
    marginTop: 6,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 4,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  footerText: {
    fontSize: 5.8,
    color: '#64748b'
  }
});

const TmvMaintenanceChecklistPDF = ({ formData = {} }) => {
  const tester = formData.testerDetails || {};
  const premises = formData.premisesDetails || {};
  const valve = formData.valveSpecification || {};
  const temp = formData.temperatureReadings || {};
  const failSafe = formData.failSafeTesting || {};
  const service = formData.mechanicalServicing || {};
  const summary = formData.summaryAndDeclarations || {};

  return (
    <Document>
      <Page size="A4" style={styles.page} orientation="portrait">
        {/* Banner */}
        <View style={styles.headerBanner}>
          <View>
            <Text style={styles.headerTitle}>Thermostatic Mixing Valve (TMV) Maintenance</Text>
            <Text style={styles.headerSubtitle}>TMV2 / TMV3 Scheme & NHS Model Engineering Specification D08</Text>
          </View>
          <View style={styles.badge}>
            <Text>{valve.tmvScheme ? valve.tmvScheme.split(' ')[0] : 'TMV3'}</Text>
          </View>
        </View>

        {/* 1 & 2: Testing Engineer & Premises */}
        <View style={styles.grid2}>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>1. Testing Organization</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Company:</Text>
              <Text style={styles.fieldValue}>{tester.companyName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Engineer:</Text>
              <Text style={styles.fieldValue}>{tester.engineerName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Telephone:</Text>
              <Text style={styles.fieldValue}>{tester.telephone || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Qualifications:</Text>
              <Text style={styles.fieldValue}>{tester.qualifications || 'Competent Person'}</Text>
            </View>
          </View>

          <View style={styles.panel}>
            <Text style={styles.panelTitle}>2. Facility / Premises</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Site Name:</Text>
              <Text style={styles.fieldValue}>{premises.siteName || 'N/A'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Premises Category:</Text>
              <Text style={styles.fieldValue}>{premises.siteType || 'Healthcare'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Address:</Text>
              <Text style={styles.fieldValue}>{premises.siteAddress || 'N/A'}, {premises.postCode || ''}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Date of Inspection:</Text>
              <Text style={styles.fieldValue}>{premises.testDate || 'N/A'}</Text>
            </View>
          </View>
        </View>

        {/* 3: Valve Specification */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>3. Valve Identification & Application Designation</Text>
          <View style={styles.grid2}>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Valve Asset Tag:</Text>
                <Text style={styles.fieldValue}>{valve.valveTagNumber || 'N/A'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Room / Location:</Text>
                <Text style={styles.fieldValue}>{valve.valveLocation || 'N/A'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>TMV Scheme:</Text>
                <Text style={styles.fieldValue}>{valve.tmvScheme || 'TMV3'}</Text>
              </View>
            </View>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Application:</Text>
                <Text style={styles.fieldValue}>{valve.applicationKey || 'Washbasin'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Make & Model:</Text>
                <Text style={styles.fieldValue}>{valve.valveMakeModel || 'N/A'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Valve Size:</Text>
                <Text style={styles.fieldValue}>{valve.valveSizeMm || '15mm'}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* 4 & 5: Temperatures & Fail-Safe Test */}
        <View style={styles.grid2}>
          <View style={styles.panel}>
            <Text style={styles.panelTitle}>4. Temperature & Pressure Readings</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Hot Water Supply:</Text>
              <Text style={styles.fieldValue}>{temp.hotSupplyTempC}°C ({temp.hotSupplyDynamicPressureBar || 2.0} Bar)</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Cold Water Supply:</Text>
              <Text style={styles.fieldValue}>{temp.coldSupplyTempC}°C ({temp.coldSupplyDynamicPressureBar || 2.0} Bar)</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Set Mixed Outlet Temp:</Text>
              <Text style={[styles.fieldValue, { fontWeight: 'bold' }]}>{temp.setMixedOutletTempC}°C</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Max Allowed Temp:</Text>
              <Text style={styles.fieldValue}>{temp.maxAllowableTempC}°C</Text>
            </View>
          </View>

          <View style={styles.panel}>
            <Text style={styles.panelTitle}>5. Cold Isolation Fail-Safe Test</Text>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Response Time:</Text>
              <Text style={[styles.fieldValue, styles.passText]}>{failSafe.failSafeResponseTimeSec} sec (Max 1.5s)</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Residual Seepage:</Text>
              <Text style={styles.fieldValue}>{failSafe.residualSeepageMl || 0} ml in 30s (Max 120ml)</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Fail-Safe Verdict:</Text>
              <Text style={[styles.fieldValue, styles.passText]}>{failSafe.failSafeResult || 'Pass'}</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Restored Mixed Temp:</Text>
              <Text style={styles.fieldValue}>{failSafe.restoredMixedTempC}°C (Stable: {failSafe.tempStabilityWithin2C || 'Yes'})</Text>
            </View>
          </View>
        </View>

        {/* 6: Mechanical Servicing */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>6. Mechanical Servicing & Backflow Prevention</Text>
          <View style={styles.grid2}>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Strainers Inspected/Cleaned:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{service.strainersCleaned || 'Yes'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Check Valves Backflow Test:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{service.checkValvesTested || 'Pass - Sound'}</Text>
              </View>
            </View>
            <View style={{ flex: 1 }}>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Cartridge Descaled:</Text>
                <Text style={styles.fieldValue}>{service.descalingRequired || 'No - Clean'}</Text>
              </View>
              <View style={styles.fieldRow}>
                <Text style={styles.fieldLabel}>Isolation Valves Free:</Text>
                <Text style={[styles.fieldValue, styles.passText]}>{service.isolationValvesOperating || 'Yes'}</Text>
              </View>
            </View>
          </View>
        </View>

        {/* 7: Summary & Verdict */}
        <View style={styles.panel}>
          <Text style={styles.panelTitle}>7. Overall Assessment & Compliance Record</Text>
          <View style={styles.fieldRow}>
            <Text style={styles.fieldLabel}>Compliance Verdict:</Text>
            <Text style={[styles.fieldValue, { fontWeight: 'bold', color: '#0e7490' }]}>{summary.overallResult || 'SATISFACTORY'}</Text>
          </View>
          <Text style={{ fontSize: 6.5, color: '#334155', marginTop: 2 }}>
            {summary.remedialActionNotes || 'Valve operating fully within statutory TMV limits. Fail-safe isolation verified.'}
          </Text>
          <View style={[styles.fieldRow, { marginTop: 3 }]}>
            <Text style={styles.fieldLabel}>Next Inspection Due:</Text>
            <Text style={[styles.fieldValue, { fontWeight: 'bold' }]}>{summary.nextInspectionDue || '6 Months'}</Text>
          </View>
        </View>

        {/* Signatures */}
        <View style={styles.signaturesPanel}>
          <View style={styles.signatureBox}>
            <Text style={styles.panelTitle}>TMV Testing Engineer</Text>
            <View style={{ height: 32, justifyContent: 'center' }}>
              <Text style={{ fontSize: 7, fontWeight: 'bold' }}>{tester.engineerName || 'Testing Engineer'}</Text>
              <Text style={{ fontSize: 6, color: '#64748b' }}>TMV Compliance Certified</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Date:</Text>
              <Text style={styles.fieldValue}>{premises.testDate || new Date().toISOString().split('T')[0]}</Text>
            </View>
          </View>

          <View style={styles.signatureBox}>
            <Text style={styles.panelTitle}>Facility Duty Holder / Witness</Text>
            <View style={{ height: 32, justifyContent: 'center' }}>
              <Text style={{ fontSize: 7, fontWeight: 'bold' }}>{premises.siteName || 'Responsible Person'}</Text>
              <Text style={{ fontSize: 6, color: '#64748b' }}>Inspection received & logged</Text>
            </View>
            <View style={styles.fieldRow}>
              <Text style={styles.fieldLabel}>Date:</Text>
              <Text style={styles.fieldValue}>{premises.testDate || new Date().toISOString().split('T')[0]}</Text>
            </View>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            Thermostatic Mixing Valve Scheme (TMV2 / TMV3) • NHS D08 Statutory Compliance Checklist
          </Text>
          <Text style={styles.footerText}>
            Generated: {new Date().toLocaleDateString('en-GB')}
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default TmvMaintenanceChecklistPDF;
